import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AuthorCardCarouselWithGraphQL } from 'javascripts/components/author_card_carousel_with_graphql';
import { BookCardCarouselWithGraphQLErrors } from 'javascripts/components/book_card_carousel_with_graphql_errors';
import { Breadcrumbs } from 'javascripts/components/breadcrumbs';
import { CategoryCollectionsShelf } from 'javascripts/components/category_collections_shelf';
import { CollapsibleDropdownMenu } from 'javascripts/components/collapsible_dropdown_menu';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { PillArray } from 'javascripts/components/pill_array';
import { TagFollowButton } from 'javascripts/components/tag_follow_button';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';
import { bubEvents } from 'shared/events/bub_events';
import { graphQLQueries } from 'javascripts/shared/graphql/queries';
import { siteLinkData } from 'shared/utils/site_link_data';
import { userData } from 'javascripts/shared/utils/user_data';
import { useMediaQuery } from 'react-responsive';
import upArrowIcon from 'images/arrow-up';

import styles from 'stylesheets/apps/category_app';

const MAX_CAROUSEL_ITEMS = 20;
const CAROUSEL_TITLE_ELEMENT = 'h2';
const UNDER_TWO_DOLLARS_CAROUSEL_MIN_SIZE = 7;
const RECENTLY_ADDED_CAROUSEL_MIN_SIZE = 10;
const OUR_FAVORITES_CAROUSEL_MIN_SIZE = 4;
const OUR_FAVORITES_CAROUSEL_VIEW_ALL_SIZE = 6;
const NEW_AND_NOTEWORTHY_CAROUSEL_MIN_SIZE = 5;

export function CategoryApp({ tag, hideNewAndNoteworthyCarousel, showBookLists }) {
  const isMobile = useMediaQuery({ maxWidth: breakpoints.lg });
  const [showFeaturedAuthors, setShowFeaturedAuthors] = useState(tag.hasAuthors);
  const [showUnderTwoDollarCarousel, setShowUnderTwoDollarCarousel] = useState(false);
  const [showRecentlyAddedCarousel, setShowRecentlyAddedCarousel] = useState(false);
  const [showOurFavoritesCarousel, setShowOurFavoritesCarousel] = useState(false);
  const [showOurFavoritesCarouselViewAll, setShowOurFavoritesCarouselViewAll] = useState(false);
  const [showNewAndNoteworthyCarousel, setShowNewAndNoteworthyCarousel] = useState(false);
  const [showRelatedCollectionsCarousel, setShowRelatedCollectionsCarousel] = useState(false);
  const [seed] = useState(Math.floor(Math.random() * 100) + 1);
  const [ourFavoritesSeed] = useState(Math.floor(Math.random() * 100) + 1);
  const [followErrorAction, setFollowErrorAction] = useState();

  function scrollToTop() {
    window.scrollTo(0, 0);
  }

  function renderScrollToTop() {
    return (
      <div className={styles.scrollToTopSection}>
        <ChirpButton
          onClick={scrollToTop}
          displayType='link'
          fontSize='small'
          label='Back to Top'
          icon={upArrowIcon}
          iconSize='small'
          class={styles.scrollToTopSection}
        />
      </div>
    );
  }

  function trackCategoryClick(category) {
    return () => {
      bubEvents.track('Featured Category Buttons', 'Clicked card', {
        label: 'Category',
        value: category.displayName,
        id: category.id,
      });
    };
  }

  function renderBreadcrumbs() {
    return (
      <div className={styles.breadcrumbs}>
        <Breadcrumbs levels={tag.breadcrumbs} />
      </div>
    );
  }

  function renderFollowButton() {
    if (!userData.isLoggedIn()) {
      return null;
    }

    return (
      <div className={styles.followButton}>
        <TagFollowButton
          tag={tag}
          onError={setFollowErrorAction}
          onFollowChange={() => setFollowErrorAction(null)}
          trackingCategory='Featured category page'
        />
      </div>
    );
  }

  function renderFollowError() {
    if (_.isNil(followErrorAction)) {
      return null;
    }

    return (
      <div className={styles.followError}>
        An error occurred while {followErrorAction} {tag.displayName}. To modify your category preferences{' '}
        <a className={styles.errorLink} href={siteLinkData.getPath('categoryPreferences')}>
          click here
        </a>
        .
      </div>
    );
  }

  function renderHeader() {
    return (
      <div className={styles.headerSection}>
        {renderBreadcrumbs()}
        <div className={styles.titleSection}>
          <h1 className={styles.headerText}>{tag.displayName}</h1>
          {renderFollowButton()}
        </div>
        {renderFollowError()}
      </div>
    );
  }

  function renderFeaturedCategories() {
    if (_.isEmpty(tag.featuredCategoryChildren)) {
      return null;
    }

    if (isMobile) {
      return <CollapsibleDropdownMenu title='Featured Categories' items={tag.featuredCategoryChildren} />;
    }

    return (
      <div className={styles.featuredCategoriesSection}>
        <div className={styles.featuredCategoriesSectionHeader}>Featured Categories</div>
        <div className={styles.featuredCategoriesContainer}>
          {tag.featuredCategoryChildren.map(subCategory => (
            <a
              className={styles.featuredCategoriesElement}
              href={subCategory.url}
              key={subCategory.id}
              onClick={trackCategoryClick(subCategory)}
            >
              {subCategory.displayName}
            </a>
          ))}
        </div>
      </div>
    );
  }

  function renderLimitedTimeDealsCarousel(carouselPosition) {
    const carouselTitle = 'Limited-Time Deals';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'LIMITED_TIME_DEALS',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
          }}
          responseDataType='audiobook'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          linkHref={`/view-all-audiobook-deals?tags[]=${tag.slug}&sort=POPULARITY_DESC`}
          linkText='View All'
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderRecentlyAddedDealsCarousel(carouselPosition) {
    const carouselTitle = 'Recently Added Deals';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'RECENTLY_ADDED_DEALS',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
          }}
          responseDataType='audiobook'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          linkHref={`/view-all-audiobook-deals?tags[]=${tag.slug}&sort=START_DATE_DESC`}
          linkText='View All'
          minSize={RECENTLY_ADDED_CAROUSEL_MIN_SIZE}
          onDataLoad={response =>
            setShowRecentlyAddedCarousel(
              response.featuredCategoryAudiobooks.totalCount >= RECENTLY_ADDED_CAROUSEL_MIN_SIZE,
            )
          }
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderCollectionsCarousel(carouselPosition) {
    if (!showBookLists) { return null; }

    const carouselTitle = 'Book Lists';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <CategoryCollectionsShelf
          tagSlug={tag.slug}
          trackingCategory={trackingCategory}
          linkHref={_.isEmpty(tag.featuredCategoryChildren) ? null : `/audiobook-lists?tag=${tag.slug}`}
          onDataLoad={hasCollections => setShowRelatedCollectionsCarousel(hasCollections)}
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
        />
      </div>
    );
  }

  function renderTrendingDealsUnderTwoDollarsCarousel(carouselPosition) {
    const carouselTitle = 'Trending Deals Under $2';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'TRENDING_DEALS_UNDER_TWO_DOLLARS',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
          }}
          linkHref={`/view-all-audiobook-deals?tags[]=${tag.slug}&sort=POPULARITY_DESC&maxPrice=199`}
          linkText='View All'
          minSize={UNDER_TWO_DOLLARS_CAROUSEL_MIN_SIZE}
          responseDataType='audiobook'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          onDataLoad={response =>
            setShowUnderTwoDollarCarousel(
              response.featuredCategoryAudiobooks.objects.length >= UNDER_TWO_DOLLARS_CAROUSEL_MIN_SIZE,
            )
          }
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderOurFavoritesCarousel(carouselPosition) {
    const carouselTitle = 'Popular & Trending — up to 40% off';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'OUR_FAVORITES',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
            seed: ourFavoritesSeed,
          }}
          responseDataType='audiobook'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          onDataLoad={response => {
            setShowOurFavoritesCarousel(
              response.featuredCategoryAudiobooks.totalCount >= OUR_FAVORITES_CAROUSEL_MIN_SIZE,
            );

            setShowOurFavoritesCarouselViewAll(
              response.featuredCategoryAudiobooks.totalCount >= OUR_FAVORITES_CAROUSEL_VIEW_ALL_SIZE,
            );
          }}
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          linkHref={`/favorites/${tag.slug}`}
          linkText={showOurFavoritesCarouselViewAll ? 'View All' : ''}
          minSize={OUR_FAVORITES_CAROUSEL_MIN_SIZE}
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderHighestRatedCarousel(carouselPosition) {
    const carouselTitle = 'Highest Rated';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'HIGHEST_RATED',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
          }}
          responseDataType='audiobook'
          linkHref={`/tags/${tag.slug}?sort=highest_rated`}
          linkText='View All'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderNewAndNoteworthyCarousel(carouselPosition) {
    if (hideNewAndNoteworthyCarousel) {
      return null;
    }

    const carouselTitle = 'New & Noteworthy';
    const trackingCategory = `Featured category page ${carouselTitle} Carousel`;
    return (
      <div className={styles.sectionItem} key={trackingCategory} role='carousel'>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.featuredCategoryAudiobooks.objects}
          query={graphQLQueries.featuredCategoryAudiobooks}
          queryParams={{
            tagId: tag.id,
            carousel: 'NEW_AND_NOTEWORTHY',
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
          }}
          responseDataType='audiobook'
          title={carouselTitle}
          trackingCategory={trackingCategory}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          minSize={NEW_AND_NOTEWORTHY_CAROUSEL_MIN_SIZE}
          onDataLoad={response =>
            setShowNewAndNoteworthyCarousel(
              response.featuredCategoryAudiobooks.totalCount >= NEW_AND_NOTEWORTHY_CAROUSEL_MIN_SIZE,
            )
          }
          linkHref={`/new-and-noteworthy/${tag.slug}`}
          linkText='View All'
          showCoverButtons
          showStarRatings
        />
      </div>
    );
  }

  function renderFeaturedAuthorsCarousel(carouselPosition) {
    if (!showFeaturedAuthors) {
      return null;
    }

    const trackingCategory = `${tag.displayName} Featured Authors Carousel`;
    return (
      <div className={styles.sectionItem}>
        <AuthorCardCarouselWithGraphQL
          fetchResponseItems={response => response.tagSuggestedPeople.objects}
          query={graphQLQueries.tagSuggestedPeople}
          queryParams={{ seed, tag: tag.slug, page: 1 }}
          title='Featured Authors'
          trackingCategory={trackingCategory}
          slideSize={isMobile ? 2 : 3}
          arrowTrackingCategory={trackingCategory}
          hideEmptyCarousel
          showButtons={userData.isLoggedIn()}
          titleElement={CAROUSEL_TITLE_ELEMENT}
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          onDataLoad={response => setShowFeaturedAuthors(response.tagSuggestedPeople.objects.length > 0)}
        />
      </div>
    );
  }

  function renderSuggestedTags() {
    if (_.isEmpty(tag.suggestedTags) && _.isEmpty(tag.viewAllPill)) {
      return null;
    }

    const labels = _.compact(_.concat([], tag.suggestedTags, tag.viewAllPill));

    return (
      <div>
        {!_.isEmpty(tag.suggestedTags) && <div className={styles.ymalHeader}>You might also like</div>}
        <PillArray
          labels={labels}
          size='small'
          trackingCategory='You might also like'
          trackingLabel='Category'
          altLastPillColor
        />
      </div>
    );
  }

  const conditionalCarouselsCount = [
    showUnderTwoDollarCarousel,
    showRelatedCollectionsCarousel,
    showRecentlyAddedCarousel,
    showOurFavoritesCarousel,
  ].filter(Boolean).length;
  return (
    <div className={styles.categoryApp}>
      {renderHeader()}
      {renderFeaturedCategories()}
      <div className={styles.audiobookCarouselsContainer}>
        {renderLimitedTimeDealsCarousel(1)}
        {renderOurFavoritesCarousel(2)}
        {renderRecentlyAddedDealsCarousel(2 + (showOurFavoritesCarousel ? 1 : 0))}
        {renderCollectionsCarousel(2 + (showRecentlyAddedCarousel ? 1 : 0) + (showOurFavoritesCarousel ? 1 : 0))}
        {renderTrendingDealsUnderTwoDollarsCarousel(
          2 +
            (showRecentlyAddedCarousel ? 1 : 0) +
            (showOurFavoritesCarousel ? 1 : 0) +
            (showRelatedCollectionsCarousel ? 1 : 0),
        )}
        {renderHighestRatedCarousel(2 + conditionalCarouselsCount)}
        {renderNewAndNoteworthyCarousel(3 + conditionalCarouselsCount)}
      </div>
      {renderFeaturedAuthorsCarousel(3 + conditionalCarouselsCount + (showNewAndNoteworthyCarousel ? 1 : 0))}
      {renderSuggestedTags()}
      {renderScrollToTop()}
    </div>
  );
}

CategoryApp.propTypes = {
  tag: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.array.isRequired,
    slug: PropTypes.string.isRequired,
    hasAuthors: PropTypes.bool.isRequired,
    suggestedTags: PropTypes.array,
    featuredCategoryChildren: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    viewAllPill: PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  }).isRequired,
  hideNewAndNoteworthyCarousel: PropTypes.bool.isRequired,
  showBookLists: PropTypes.bool.isRequired,
};
